import React, { useState, useEffect } from 'react';
import { callApi } from '../api/api';
import FirebaseImageUpload from '../components/FirebaseImageUpload';
import DriverMapRoute from '../components/DriverMapRoute';
import AdminCreateOrderModal from './AdminCreateOrderModal';
import { sendDirectWhatsApp, sendDirectSMS } from '../utils/directMessaging';

const OrderEditModal = ({ order, onClose, onSave, showMessage }) => {
    const [localOrder, setLocalOrder] = useState(JSON.parse(JSON.stringify(order)));
    const [newItem, setNewItem] = useState({ name: '', price: '', quantity: '' });
    const [packageVolume, setPackageVolume] = useState(order.packageVolume || '');
    const [billAmount, setBillAmount] = useState('');
    const [storeBillUrl, setStoreBillUrl] = useState(order.storeBillUrl || null);
    const [isSendingBill, setIsSendingBill] = useState(false);
    const [isScanning, setIsScanning] = useState(false);

    const handleItemChange = (itemId, field, value) => {
        const updatedItems = localOrder.items.map(item =>
            item._id === itemId ? { ...item, [field]: value } : item
        );
        setLocalOrder({ ...localOrder, items: updatedItems });
    };

    const handleRemoveItem = (itemId) => {
        setLocalOrder({ ...localOrder, items: localOrder.items.filter(item => item._id !== itemId) });
    };

    const handleSaveItems = async () => {
        try {
            const savedOrder = await callApi(`/admin/orders/${order._id}`, 'PUT', { 
                items: localOrder.items, 
                packageVolume,
                customerName: localOrder.customerName,
                customerPhone: localOrder.customerPhone,
                deliveryAddress: localOrder.deliveryAddress
            });
            onSave(savedOrder);
            showMessage('Order details updated.', 'success');
        } catch (error) {
            showMessage('Failed to update order.', 'error');
        }
    };

    const handleAddItem = () => {
        if (!newItem.name || !newItem.price || !newItem.quantity) {
            showMessage('Fill all fields.', 'error');
            return;
        }
        const updatedItems = [...localOrder.items, { ...newItem, _id: Date.now().toString() }];
        setLocalOrder({ ...localOrder, items: updatedItems });
        setNewItem({ name: '', price: '', quantity: '' });
    };

    const handleScanBill = async () => {
        if (!storeBillUrl) return;
        setIsScanning(true);
        try {
            const response = await callApi('/admin/scan-bill', 'POST', { imageUrl: storeBillUrl });
            if (response.success && response.data) {
                if (response.data.products && Array.isArray(response.data.products)) {
                    const scannedProducts = response.data.products.map((p, idx) => ({
                        _id: Date.now().toString() + idx,
                        name: p.name || '',
                        packing: p.packing || '',
                        quantity: p.quantity || 1,
                        price: p.price || 0
                    }));
                    setLocalOrder(prev => ({ ...prev, items: [...prev.items, ...scannedProducts] }));
                }
                if (response.data.total) {
                    setBillAmount(response.data.total.toString());
                }
                showMessage('Bill scanned successfully!', 'success');
            } else {
                showMessage(response.message || 'Failed to scan bill.', 'error');
            }
        } catch (error) {
            showMessage(error.message || 'Error scanning bill.', 'error');
        } finally {
            setIsScanning(false);
        }
    };

    const handleSendBill = async (channel = 'whatsapp') => {
        if (!billAmount || isNaN(billAmount) || billAmount <= 0) {
            showMessage('Please enter a valid bill amount.', 'error');
            return;
        }
        setIsSendingBill(true);
        try {
            await callApi(`/admin/orders/${order._id}/bill`, 'PUT', { total: billAmount, storeBillUrl });
            showMessage('Bill saved successfully!', 'success');
            onSave(order); // Refresh parent
            const phone = order.customerPhone || order.shippingAddress?.phone || (order.user && order.user.mobile);
            const link = `${window.location.origin}/#pay/${order.orderId}`;
            const msg = `Hi ${order.customerName || 'Customer'}, your bill for QwickMed order #${order.orderId} is ready!\nTotal Amount: ₹${billAmount}\nPlease pay securely using this link: ${link}`;
            
            if (channel === 'whatsapp' && phone) {
                sendDirectWhatsApp(phone, msg);
            } else if (channel === 'sms' && phone) {
                sendDirectSMS(phone, msg);
            } else if (channel === 'push') {
                try {
                    const res = await callApi('/admin/send-notification', 'POST', {
                        userId: order.userId || (order.user && order.user._id),
                        phone: phone,
                        title: 'Bill Ready — QwickMed',
                        body: `Your bill for ₹${billAmount} is ready. Tap to view and pay online.`,
                        data: { orderId: order.orderId }
                    });
                    showMessage(res.message || 'Push notification sent to customer!', res.success ? 'success' : 'info');
                } catch (pe) {
                    showMessage('Could not send push notification.', 'error');
                }
            }
        } catch (error) {
            showMessage('Failed to send bill.', 'error');
        } finally {
            setIsSendingBill(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex justify-center items-center z-50 p-2 sm:p-4 animate-fade-in">
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden border border-slate-100">
                <div className="bg-slate-50 px-4 sm:px-8 py-4 sm:py-6 border-b border-slate-100 flex justify-between items-center shrink-0">
                    <div>
                        <h3 className="text-lg sm:text-xl font-extrabold text-slate-800">Edit Order Details</h3>
                        <p className="text-xs sm:text-sm text-slate-500 font-mono mt-0.5">ID: {localOrder.orderId}</p>
                    </div>
                    <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-white text-slate-400 hover:text-slate-600 shadow-sm border border-slate-200 transition-colors">
                        <i className="fas fa-times text-xs"></i>
                    </button>
                </div>

                <div className="p-4 sm:p-8 overflow-y-auto flex-grow custom-scrollbar">
                    <div className="mb-4 sm:mb-6 bg-slate-50 p-3 sm:p-4 rounded-xl sm:rounded-2xl border border-slate-200">
                        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Order Information</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs font-bold text-slate-700 ml-1 block mb-1">Customer Name</label>
                                <input type="text" value={localOrder.customerName || ''} onChange={e => setLocalOrder({...localOrder, customerName: e.target.value})} placeholder="Customer Name" className="w-full p-2.5 bg-white border border-slate-200 focus:border-sky-400 rounded-xl text-xs sm:text-sm font-semibold text-slate-700 outline-none transition-all shadow-sm" />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-700 ml-1 block mb-1">Customer Phone</label>
                                <input type="text" value={localOrder.customerPhone || ''} onChange={e => setLocalOrder({...localOrder, customerPhone: e.target.value})} placeholder="Customer Phone" className="w-full p-2.5 bg-white border border-slate-200 focus:border-sky-400 rounded-xl text-xs sm:text-sm font-semibold text-slate-700 outline-none transition-all shadow-sm" />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="text-xs font-bold text-slate-700 ml-1 block mb-1">Delivery Address</label>
                                <input type="text" value={localOrder.deliveryAddress || ''} onChange={e => setLocalOrder({...localOrder, deliveryAddress: e.target.value})} placeholder="Delivery Address" className="w-full p-2.5 bg-white border border-slate-200 focus:border-sky-400 rounded-xl text-xs sm:text-sm font-semibold text-slate-700 outline-none transition-all shadow-sm" />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="text-xs font-bold text-slate-700 ml-1 block mb-1">Total Package Volume</label>
                                <input type="text" value={packageVolume} onChange={e => setPackageVolume(e.target.value)} placeholder="e.g. 500ml or 25x15x10 cm" className="w-full p-2.5 bg-white border border-slate-200 focus:border-sky-400 rounded-xl text-xs sm:text-sm font-semibold text-slate-700 outline-none transition-all shadow-sm" />
                            </div>
                        </div>
                    </div>
                    <div className="space-y-3 mb-6 max-h-[40vh] overflow-y-auto pr-1 custom-scrollbar">
                        {localOrder.items.map(item => (
                            <div key={item._id} className="flex flex-col sm:flex-row gap-2 sm:gap-3 items-start sm:items-center bg-white p-3 rounded-xl sm:rounded-2xl border border-slate-200 shadow-sm hover:border-sky-300 transition-colors">
                                <div className="flex-grow">
                                    <label className="text-[10px] uppercase font-bold text-slate-400 ml-1">Product</label>
                                    <input type="text" value={item.name} onChange={e => handleItemChange(item._id, 'name', e.target.value)} className="w-full p-2 bg-slate-50 border-transparent focus:bg-white focus:border-sky-400 rounded-xl text-sm font-semibold text-slate-700 outline-none transition-all" />
                                </div>
                                <div className="w-20">
                                    <label className="text-[10px] uppercase font-bold text-slate-400 ml-1">Qty</label>
                                    <input type="number" value={item.quantity} onChange={e => handleItemChange(item._id, 'quantity', e.target.value)} className="w-full p-2 bg-slate-50 border-transparent focus:bg-white focus:border-sky-400 rounded-xl text-sm font-bold text-slate-700 text-center outline-none transition-all" />
                                </div>
                                <div className="w-24">
                                    <label className="text-[10px] uppercase font-bold text-slate-400 ml-1">Price (₹)</label>
                                    <input type="number" value={item.price} onChange={e => handleItemChange(item._id, 'price', e.target.value)} className="w-full p-2 bg-slate-50 border-transparent focus:bg-white focus:border-sky-400 rounded-xl text-sm font-bold text-sky-600 text-center outline-none transition-all" />
                                </div>
                                <div className="w-8 flex items-end justify-center pb-2">
                                    <button onClick={() => handleRemoveItem(item._id)} className="text-red-400 hover:text-red-600 transition-colors" title="Remove Item">
                                        <i className="fas fa-trash"></i>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {(localOrder.prescriptionUrls && localOrder.prescriptionUrls.length > 0) ? (
                        <div className="bg-white rounded-2xl p-4 border border-slate-200 mb-6">
                            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Customer Uploaded Prescriptions / Lists</h4>
                            <div className="flex flex-wrap gap-3">
                                {localOrder.prescriptionUrls.map((url, idx) => (
                                    <a key={idx} href={url} target="_blank" rel="noreferrer" className="block relative group">
                                        <img src={url} alt={`Prescription ${idx + 1}`} className="w-28 h-28 object-cover rounded-xl border-2 border-slate-200 group-hover:border-sky-400 transition-colors" />
                                        <div className="absolute inset-0 bg-sky-900/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                                            <i className="fas fa-external-link-alt text-white text-xl"></i>
                                        </div>
                                    </a>
                                ))}
                            </div>
                        </div>
                    ) : localOrder.prescriptionUrl && (
                        <div className="bg-white rounded-2xl p-4 border border-slate-200 mb-6">
                            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Customer Uploaded Prescription</h4>
                            <a href={localOrder.prescriptionUrl} target="_blank" rel="noreferrer" className="inline-block relative group">
                                <img src={localOrder.prescriptionUrl} alt="Prescription" className="w-28 h-28 object-cover rounded-xl border-2 border-slate-200 group-hover:border-sky-400 transition-colors" />
                                <div className="absolute inset-0 bg-sky-900/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                                    <i className="fas fa-external-link-alt text-white text-xl"></i>
                                </div>
                            </a>
                        </div>
                    )}

                    <div className="bg-sky-50 rounded-2xl p-4 border border-sky-100 mb-6">
                        <h4 className="text-xs font-bold text-sky-700 uppercase tracking-wider mb-3 flex items-center gap-2"><i className="fas fa-plus-circle"></i> Add New Item</h4>
                        <div className="flex gap-3">
                            <input type="text" placeholder="Product Name" value={newItem.name} onChange={e => setNewItem({...newItem, name: e.target.value})} className="flex-grow p-2.5 bg-white border border-sky-200 focus:border-sky-500 rounded-xl text-sm outline-none" />
                            <input type="number" placeholder="Qty" value={newItem.quantity} onChange={e => setNewItem({...newItem, quantity: e.target.value})} className="w-20 p-2.5 bg-white border border-sky-200 focus:border-sky-500 rounded-xl text-sm text-center outline-none" />
                            <input type="number" placeholder="Price" value={newItem.price} onChange={e => setNewItem({...newItem, price: e.target.value})} className="w-24 p-2.5 bg-white border border-sky-200 focus:border-sky-500 rounded-xl text-sm text-center outline-none" />
                            <button onClick={handleAddItem} className="px-4 bg-sky-500 hover:bg-sky-600 text-white rounded-xl font-bold transition-colors shadow-md shadow-sky-500/20">Add</button>
                        </div>
                    </div>
                    
                    {localOrder.status === 'Awaiting Bill' && (
                        <div className="bg-amber-50 rounded-2xl p-5 border-2 border-amber-200 mb-6">
                            <h4 className="text-sm font-extrabold text-amber-800 mb-2">Prescription Action Required</h4>
                            <p className="text-xs text-amber-700 mb-4">Set the final bill amount for this prescription order. The customer will be prompted to pay this amount.</p>
                            
                            <div className="mb-4 bg-white p-4 rounded-xl border border-amber-100">
                                <h5 className="text-xs font-bold text-slate-500 uppercase mb-2">Attach Store Bill (Optional)</h5>
                                {storeBillUrl ? (
                                    <div className="flex flex-col items-center">
                                        <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-2">
                                            <i className="fas fa-check text-emerald-500 text-xl"></i>
                                        </div>
                                        <p className="text-xs font-bold text-emerald-600 mb-1">Bill Uploaded</p>
                                        <div className="flex gap-2 mb-2 mt-1">
                                            <button type="button" onClick={handleScanBill} disabled={isScanning} className="bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white font-bold py-1 px-3 rounded-lg text-xs shadow-sm transition-colors flex items-center gap-1">
                                                {isScanning ? <><i className="fas fa-spinner fa-spin"></i> Scanning...</> : <><i className="fas fa-magic"></i> Auto-fill</>}
                                            </button>
                                        </div>
                                        <button onClick={() => setStoreBillUrl(null)} className="text-[10px] text-red-500 font-bold underline">Remove</button>
                                    </div>
                                ) : (
                                    <FirebaseImageUpload onUploadSuccess={setStoreBillUrl} folder="store_bills" />
                                )}
                            </div>

                            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                                <div className="flex items-center flex-grow bg-white border border-amber-300 focus-within:border-amber-500 rounded-xl px-3 py-1 shadow-sm">
                                    <span className="font-black text-slate-700 text-lg mr-2">₹</span>
                                    <input 
                                        type="number" 
                                        placeholder="Enter Total Bill Amount" 
                                        value={billAmount} 
                                        onChange={e => setBillAmount(e.target.value)} 
                                        className="w-full py-2 font-bold outline-none text-slate-800 text-sm" 
                                    />
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <button 
                                        onClick={() => handleSendBill('whatsapp')} 
                                        disabled={isSendingBill}
                                        className="px-3.5 py-2.5 bg-[#25D366] hover:bg-[#128C7E] disabled:bg-emerald-300 text-white rounded-xl font-bold text-xs transition-colors shadow-sm flex items-center gap-1.5"
                                        title="Save Bill & Send via WhatsApp"
                                    >
                                        <i className="fab fa-whatsapp text-sm"></i> WhatsApp
                                    </button>
                                    <button 
                                        onClick={() => handleSendBill('sms')} 
                                        disabled={isSendingBill}
                                        className="px-3.5 py-2.5 bg-sky-500 hover:bg-sky-600 disabled:bg-sky-300 text-white rounded-xl font-bold text-xs transition-colors shadow-sm flex items-center gap-1.5"
                                        title="Save Bill & Send via Native SMS"
                                    >
                                        <i className="fas fa-comment text-xs"></i> SMS
                                    </button>
                                    <button 
                                        onClick={() => handleSendBill('push')} 
                                        disabled={isSendingBill}
                                        className="px-3.5 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white rounded-xl font-bold text-xs transition-colors shadow-sm flex items-center gap-1.5"
                                        title="Save Bill & Send App Push Notification"
                                    >
                                        <i className="fas fa-bell text-xs"></i> Push
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Dynamic Order Totals */}
                <div className="bg-white px-4 sm:px-8 py-4 border-t border-slate-100 flex justify-end">
                    <div className="w-full sm:w-1/2 md:w-1/3">
                        <div className="flex justify-between text-sm text-slate-500 mb-1">
                            <span>Subtotal:</span>
                            <span className="font-semibold">₹{localOrder.items.reduce((acc, item) => acc + (parseFloat(item.price || 0) * parseInt(item.quantity || 1)), 0).toFixed(2)}</span>
                        </div>
                        {localOrder.deliveryFee > 0 && (
                            <div className="flex justify-between text-sm text-slate-500 mb-1">
                                <span>Delivery Fee:</span>
                                <span className="font-semibold text-sky-600">+₹{localOrder.deliveryFee.toFixed(2)}</span>
                            </div>
                        )}
                        {((localOrder.discountApplied || 0) + (localOrder.walletAmountUsed || 0)) > 0 && (
                            <div className="flex justify-between text-sm text-slate-500 mb-1">
                                <span>Discounts / Wallet:</span>
                                <span className="font-semibold text-emerald-500">-₹{((localOrder.discountApplied || 0) + (localOrder.walletAmountUsed || 0)).toFixed(2)}</span>
                            </div>
                        )}
                        {localOrder.promoCode && (
                            <div className="flex justify-between text-xs text-sky-600 font-bold mb-1 bg-sky-50 px-2 py-1 rounded">
                                <span>Promo:</span>
                                <span>{localOrder.promoCode}</span>
                            </div>
                        )}
                        <div className="flex justify-between text-lg font-extrabold text-slate-800 border-t border-slate-200 mt-2 pt-2">
                            <span>Total:</span>
                            <span className="text-sky-600">₹{(localOrder.items.reduce((acc, item) => acc + (parseFloat(item.price || 0) * parseInt(item.quantity || 1)), 0) + (localOrder.deliveryFee || 0) - (localOrder.discountApplied || 0) - (localOrder.walletAmountUsed || 0)).toFixed(2)}</span>
                        </div>
                    </div>
                </div>

                <div className="bg-slate-50 px-8 py-5 border-t border-slate-100 flex justify-end gap-3">
                    <button onClick={onClose} className="px-6 py-2.5 rounded-xl border border-slate-200 font-bold text-slate-600 hover:bg-white transition-colors">Cancel</button>
                    <button onClick={handleSaveItems} className="px-8 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-extrabold shadow-lg shadow-emerald-500/20 transition-all transform hover:-translate-y-0.5">Save Changes</button>
                </div>
            </div>
        </div>
    );
};

const AdminOrders = ({ showMessage }) => {
    const [orders, setOrders] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [drivers, setDrivers] = useState([]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const latestOrderIdRef = React.useRef(null);
    const [orderStatuses] = useState(['Awaiting Bill', 'Pending', 'Payment Verification Pending', 'Processing', 'Shipped', 'Out for Delivery', 'Delivered', 'Cancelled']);
    const [trackingOrderId, setTrackingOrderId] = useState(null);
    
    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Hardcoded Store Location
    const storeLocation = { lat: 19.0760, lng: 72.8777 }; // Example: Mumbai

    const playAlarm = () => {
        try {
            const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3');
            audio.play().catch(e => {
                console.log("Audio play failed, falling back to beep", e);
                const context = new (window.AudioContext || window.webkitAudioContext)();
                const oscillator = context.createOscillator();
                const gainNode = context.createGain();
                oscillator.type = 'sine';
                oscillator.frequency.setValueAtTime(880, context.currentTime); // A5
                gainNode.gain.setValueAtTime(0.5, context.currentTime);
                oscillator.connect(gainNode);
                gainNode.connect(context.destination);
                oscillator.start();
                
                // Beep pattern
                setTimeout(() => { oscillator.frequency.setValueAtTime(1046.50, context.currentTime); }, 150); // C6
                setTimeout(() => { oscillator.stop(); }, 400);
            });
        } catch (e) {
            console.log("Audio alarm failed to play", e);
        }
    };

    const fetchDrivers = async () => {
        try {
            const res = await callApi('/admin/drivers');
            if (res.success) {
                setDrivers(res.drivers);
            }
        } catch (error) {
            console.error("Failed to fetch drivers", error);
        }
    };

    const fetchOrders = async (isPolling = false) => {
        if (!isPolling) setIsLoading(true);
        try {
            const data = await callApi('/admin/orders');
            // Sort newest first
            if (data && Array.isArray(data)) {
                data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            }
            setOrders(data);
            
            if (data && data.length > 0) {
                const currentTopId = data[0]._id;
                // If we are polling, and we already had a top order, and the new top order is different
                if (isPolling && latestOrderIdRef.current && latestOrderIdRef.current !== currentTopId) {
                    playAlarm();
                    showMessage('New order received!', 'success');
                }
                latestOrderIdRef.current = currentTopId;
            }
        } catch (error) {
            console.error("Failed to fetch orders", error);
        } finally {
            if (!isPolling) setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
        fetchDrivers();
        
        // Poll every 15 seconds
        const pollInterval = setInterval(() => {
            fetchOrders(true);
        }, 15000);
        
        return () => clearInterval(pollInterval);
    }, []);

    const assignDriver = async (orderId, driverId) => {
        if (!driverId) return;
        try {
            const res = await callApi(`/admin/orders/${orderId}/assign-driver`, 'PUT', { driverId });
            showMessage('Driver assigned successfully', 'success');
            fetchOrders();
            if (res && res.driverMobile) {
                // Formatting mobile to ensure it has country code if missing
                let mobile = res.driverMobile;
                if (!mobile.startsWith('+') && mobile.length === 10) {
                    mobile = '+91' + mobile;
                }
                const message = `Hi ${res.driverName || 'Driver'}, you have been assigned a new delivery for Order ${res.order?.orderId || orderId}. Please check your QwickMed Driver Dashboard for details!`;
                window.open(`https://wa.me/${mobile.replace('+', '')}?text=${encodeURIComponent(message)}`, '_blank');
            }
        } catch (error) {
            showMessage('Failed to assign driver.', 'error');
        }
    };

    const updateStatus = async (orderId, newStatus) => {
        try {
            await callApi(`/admin/orders/${orderId}/status`, 'PUT', { status: newStatus });
            showMessage(`Order marked as ${newStatus}`, 'success');
            fetchOrders();
        } catch (error) {
            showMessage('Failed to update status.', 'error');
        }
    };

    const getOrderPhone = (order) => order.customerPhone || order.shippingAddress?.phone || (order.user && order.user.mobile) || '';

    const handleDirectWhatsAppOrder = (order, customMsg) => {
        const phone = getOrderPhone(order);
        if (!phone) {
            showMessage('No customer mobile number found for this order.', 'error');
            return;
        }
        const customerName = order.customerName || order.shippingAddress?.name || 'Customer';
        const link = `${window.location.origin}/#pay/${order.orderId}`;
        let message = customMsg;
        if (!message) {
            let itemListText = '';
            if (order.items && order.items.length > 0) {
                itemListText = '\n\nBill Items:\n' + order.items.map(item => `- ${item.name} ${item.packing ? `(${item.packing}) ` : ''}x${item.quantity} = ₹${(item.price * item.quantity).toFixed(2)}`).join('\n') + '\n\n';
            }
            if (order.status === 'Pending' || order.status === 'Awaiting Bill') {
                message = `Hi ${customerName}, your medicine order #${order.orderId} from QwickMed is ready!${itemListText}Total Amount: ₹${order.total}\nPay online safely here: ${link}`;
            } else {
                message = `Hi ${customerName}, update regarding your QwickMed Order #${order.orderId}:\nStatus: ${order.status}\nTotal: ₹${order.total}\nNeed assistance? Reply here!`;
            }
        }
        try {
            sendDirectWhatsApp(phone, message);
            showMessage('Opening WhatsApp to send order update...', 'info');
        } catch (e) {
            showMessage(e.message || 'Failed to open WhatsApp', 'error');
        }
    };

    const handleDirectSMSOrder = (order, customMsg) => {
        const phone = getOrderPhone(order);
        if (!phone) {
            showMessage('No customer mobile number found for this order.', 'error');
            return;
        }
        const customerName = order.customerName || order.shippingAddress?.name || 'Customer';
        const link = `${window.location.origin}/#pay/${order.orderId}`;
        let message = customMsg;
        if (!message) {
            if (order.status === 'Pending' || order.status === 'Awaiting Bill') {
                message = `Hi ${customerName}, your QwickMed order #${order.orderId} is ready! Amount: Rs.${order.totalAmount || order.total}. Pay link: ${link}`;
            } else {
                message = `Hi ${customerName}, your QwickMed order #${order.orderId} status is: ${order.status}.`;
            }
        }
        try {
            sendDirectSMS(phone, message);
            showMessage('Opening native SMS app...', 'info');
        } catch (e) {
            showMessage(e.message || 'Failed to open SMS app', 'error');
        }
    };

    const handleDirectPushOrder = async (order) => {
        try {
            const phone = getOrderPhone(order);
            const customerName = order.customerName || order.shippingAddress?.name || 'Customer';
            let message = '';
            if (order.status === 'Pending' || order.status === 'Awaiting Bill') {
                message = `Hi ${customerName}, your QwickMed order #${order.orderId} is ready! Amount: ₹${order.total}.`;
            } else {
                message = `Hi ${customerName}, your QwickMed order #${order.orderId} status is: ${order.status}.`;
            }
            const res = await callApi('/admin/send-notification', 'POST', {
                userId: order.userId || (order.user && order.user._id),
                phone: phone,
                title: 'Order Update — QwickMed',
                body: message,
                data: { orderId: order.orderId }
            });
            showMessage(res.message || 'Push notification sent!', res.success ? 'success' : 'info');
        } catch (e) {
            showMessage('Could not send push notification.', 'error');
        }
    };

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center h-full min-h-[400px]">
                <i className="fas fa-circle-notch fa-spin text-4xl text-sky-500 mb-4"></i>
                <p className="text-slate-400 font-medium">Loading orders...</p>
            </div>
        );
    }

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentOrders = orders.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(orders.length / itemsPerPage);

    return (
        <div className="space-y-6 animate-fade-in">
            {showCreateModal && (
                <AdminCreateOrderModal
                    onClose={() => setShowCreateModal(false)}
                    onSuccess={() => fetchOrders()}
                    showMessage={showMessage}
                />
            )}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-2">
                <div>
                    <h2 className="text-xl sm:text-2xl font-extrabold text-slate-800">Order Management</h2>
                    <p className="text-xs text-slate-500 font-medium mt-0.5">{orders.length} total orders</p>
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="flex-1 sm:flex-initial bg-sky-600 text-white px-4 py-2 text-xs sm:text-sm font-bold rounded-xl shadow-sm hover:bg-sky-700 transition-colors flex items-center justify-center gap-2"
                    >
                        <i className="fas fa-plus"></i> Create Order
                    </button>
                    <button className="bg-white px-3.5 py-2 text-xs sm:text-sm font-bold text-slate-600 rounded-xl border border-slate-200 shadow-sm hover:border-slate-300">
                        <i className="fas fa-filter mr-1.5"></i> Filter
                    </button>
                </div>
            </div>
            
            <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
                <div className="overflow-x-hidden">
                    <table className="w-full text-left border-collapse block sm:table">
                        <thead className="bg-slate-50 border-b border-slate-100 hidden sm:table-header-group">
                            <tr className="block sm:table-row">
                                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider block sm:table-cell">Order ID</th>
                                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider block sm:table-cell">Customer</th>
                                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider block sm:table-cell">Amount</th>
                                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider block sm:table-cell">Status</th>
                                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right block sm:table-cell">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 sm:divide-slate-50 block sm:table-row-group">
                            {currentOrders.map(order => (
                                <React.Fragment key={order._id}>
                                <tr className="hover:bg-slate-50/50 transition-colors group block sm:table-row p-4 sm:p-0">
                                    <td className="px-0 sm:px-6 py-2 sm:py-5 block sm:table-cell border-b sm:border-0 border-slate-50 sm:align-middle">
                                        <span className="sm:hidden font-bold text-xs text-slate-400 uppercase mr-2">Order ID:</span>
                                        <span className="font-mono text-sm font-bold text-slate-700 bg-slate-100 px-2 py-1 rounded-md">{order.orderId}</span>
                                    </td>
                                    <td className="px-0 sm:px-6 py-2 sm:py-5 block sm:table-cell border-b sm:border-0 border-slate-50 sm:align-middle">
                                        <div className="flex sm:flex-col items-center sm:items-start justify-between sm:justify-start">
                                            <span className="sm:hidden font-bold text-xs text-slate-400 uppercase">Customer:</span>
                                            <div>
                                                <p className="font-bold text-slate-800 text-right sm:text-left">{order.customerName || order.shippingAddress?.name || 'Guest User'}</p>
                                                <div className="flex items-center gap-1.5 mt-0.5 justify-end sm:justify-start">
                                                    <span className="text-[11px] text-slate-500 font-medium font-mono">{getOrderPhone(order) || 'No Mobile'}</span>
                                                    {getOrderPhone(order) && (
                                                        <div className="flex items-center gap-1 ml-1">
                                                            <button
                                                                onClick={() => handleDirectWhatsAppOrder(order)}
                                                                title="Send WhatsApp message directly from browser/app"
                                                                className="w-5 h-5 flex items-center justify-center rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors text-[11px]"
                                                            >
                                                                <i className="fab fa-whatsapp"></i>
                                                            </button>
                                                            <button
                                                                onClick={() => handleDirectSMSOrder(order)}
                                                                title="Send SMS message directly from browser/app"
                                                                className="w-5 h-5 flex items-center justify-center rounded bg-sky-100 text-sky-700 hover:bg-sky-200 transition-colors text-[9px]"
                                                            >
                                                                <i className="fas fa-comment"></i>
                                                            </button>
                                                            <button
                                                                onClick={() => handleDirectPushOrder(order)}
                                                                title="Send real-time push notification to customer's app"
                                                                className="w-5 h-5 flex items-center justify-center rounded bg-violet-100 text-violet-700 hover:bg-violet-200 transition-colors text-[9px]"
                                                            >
                                                                <i className="fas fa-bell"></i>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                                <p className="text-[11px] text-slate-400 font-medium uppercase mt-0.5 text-right sm:text-left">{(order.orderDate || order.createdAt) ? new Date(order.orderDate || order.createdAt).toLocaleDateString('en-GB') + ' ' + new Date(order.orderDate || order.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : 'INVALID DATE'}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-0 sm:px-6 py-2 sm:py-5 block sm:table-cell border-b sm:border-0 border-slate-50 sm:align-middle">
                                        <div className="flex sm:flex-col items-center sm:items-start justify-between sm:justify-start">
                                            <span className="sm:hidden font-bold text-xs text-slate-400 uppercase">Amount:</span>
                                            <div className="text-right sm:text-left">
                                                <p className="font-black text-sky-600 text-lg">₹{order.totalAmount || order.total}</p>
                                                {order.packageVolume && (
                                                    <p className="text-[10px] text-slate-500 font-medium uppercase mt-0.5"><i className="fas fa-box text-slate-400 mr-1"></i> {order.packageVolume}</p>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-0 sm:px-6 py-2 sm:py-5 block sm:table-cell border-b sm:border-0 border-slate-50 sm:align-middle">
                                        <div className="flex sm:block justify-between items-start sm:items-start">
                                            <span className="sm:hidden font-bold text-xs text-slate-400 uppercase mt-1.5">Status:</span>
                                            <div className="flex flex-col items-end sm:items-start gap-2 min-w-0">
                                                <div className="relative w-44 sm:w-48">
                                                    <select 
                                                        value={order.status} 
                                                        onChange={(e) => updateStatus(order._id, e.target.value)}
                                                        className={`appearance-none w-full text-[11px] font-extrabold px-3 py-2 pr-7 rounded-xl border-2 outline-none cursor-pointer transition-all truncate ${
                                                            order.status === 'Awaiting Bill' ? 'bg-fuchsia-50 border-fuchsia-200 text-fuchsia-700 hover:border-fuchsia-300' :
                                                            order.status === 'Payment Verification Pending' ? 'bg-purple-50 border-purple-200 text-purple-700 hover:border-purple-300' :
                                                            order.status === 'Delivered' ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:border-emerald-300' :
                                                            order.status === 'Cancelled' ? 'bg-red-50 border-red-200 text-red-700 hover:border-red-300' :
                                                            order.status === 'Shipped' || order.status === 'Out for Delivery' ? 'bg-sky-50 border-sky-200 text-sky-700 hover:border-sky-300' :
                                                            'bg-amber-50 border-amber-200 text-amber-700 hover:border-amber-300'
                                                        }`}
                                                    >
                                                        {orderStatuses.map(s => <option key={s} value={s}>{s}</option>)}
                                                    </select>
                                                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-slate-400">
                                                        <i className="fas fa-chevron-down text-[10px]"></i>
                                                    </div>
                                                </div>
                                                
                                                {(order.status === 'Processing' || order.status === 'Pending' || order.status === 'Shipped' || order.status === 'Out for Delivery') && order.deliveryMethod === 'Home Delivery' && (
                                                    <div className="w-44 sm:w-48">
                                                        <select
                                                            value={order.deliveryDriver || ''}
                                                            onChange={(e) => assignDriver(order._id, e.target.value)}
                                                            className="w-full text-[10px] font-bold px-2 py-1.5 rounded-lg border border-slate-200 text-slate-600 bg-white"
                                                        >
                                                            <option value="">🚗 Assign Driver...</option>
                                                            {drivers.map(driver => (
                                                                <option key={driver._id} value={driver._id}>{driver.name}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-0 sm:px-6 py-3 sm:py-5 text-right space-y-2 block sm:table-cell sm:align-middle">
                                        <button 
                                            onClick={() => setSelectedOrder(order)} 
                                            className="bg-white text-sky-600 border border-sky-100 hover:bg-sky-50 hover:border-sky-200 font-bold text-xs px-3 py-1.5 rounded-lg transition-all shadow-sm group-hover:shadow block ml-auto"
                                        >
                                            <i className="fas fa-pen fa-sm mr-1"></i> Edit Details
                                        </button>
                                        {order.status === 'Pending' && (
                                            <div className="space-y-1 pt-1 ml-auto w-fit">
                                                <button 
                                                    onClick={() => handleDirectWhatsAppOrder(order)}
                                                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-xs px-2.5 py-1.5 rounded-lg transition-all shadow-sm flex items-center justify-center gap-1.5"
                                                    title="Send bill payment link via WhatsApp directly"
                                                >
                                                    <i className="fab fa-whatsapp text-sm"></i> WhatsApp Bill
                                                </button>
                                                <div className="flex gap-1 justify-end">
                                                    <button 
                                                        onClick={() => handleDirectSMSOrder(order)}
                                                        className="bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100 font-bold text-[10px] px-2 py-1 rounded-md transition-all flex items-center gap-1"
                                                        title="Send bill payment link via native SMS app"
                                                    >
                                                        <i className="fas fa-comment-dots text-[9px]"></i> SMS
                                                    </button>
                                                    <button 
                                                        onClick={async () => {
                                                            try {
                                                                const phone = order.customerPhone || (order.user && order.user.mobile);
                                                                const res = await callApi('/admin/send-notification', 'POST', {
                                                                    userId: order.userId || (order.user && order.user._id),
                                                                    phone: phone,
                                                                    title: 'Bill Ready — QwickMed',
                                                                    body: `Your bill for ₹${order.total} is ready. Tap to pay online.`,
                                                                    data: { orderId: order.orderId }
                                                                });
                                                                showMessage(res.message || 'Push notification sent!', res.success ? 'success' : 'info');
                                                            } catch (e) {
                                                                showMessage('Could not send push notification.', 'error');
                                                            }
                                                        }}
                                                        className="bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100 font-bold text-[10px] px-2 py-1 rounded-md transition-all flex items-center gap-1"
                                                        title="Send real-time push notification to customer's app"
                                                    >
                                                        <i className="fas fa-bell text-[9px]"></i> Push
                                                    </button>
                                                    <button 
                                                        onClick={() => {
                                                            const link = `${window.location.origin}/#pay/${order.orderId}`;
                                                            let itemListText = '';
                                                            if (order.items && order.items.length > 0) {
                                                                itemListText = '\n\nBill Details:\n' + order.items.map(item => `- ${item.name} ${item.packing ? `(${item.packing}) ` : ''}x${item.quantity} = ₹${(item.price * item.quantity).toFixed(2)}`).join('\n') + '\n\n';
                                                            }
                                                            navigator.clipboard.writeText(`Hi ${order.customerName || ''}, your bill from QwickMed is ready!${itemListText}Please pay ₹${order.totalAmount || order.total} using this link: ${link}`);
                                                            showMessage('Payment link copied to clipboard!', 'success');
                                                        }}
                                                        className="bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100 font-bold text-[10px] px-2 py-1 rounded-md transition-all flex items-center gap-1"
                                                        title="Copy Payment link"
                                                    >
                                                        <i className="fas fa-link text-[9px]"></i> Link
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        {(order.status === 'Out for Delivery' || order.status === 'Shipped') && (
                                            <button 
                                                onClick={() => setTrackingOrderId(trackingOrderId === order._id ? null : order._id)}
                                                className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 font-bold text-xs px-3 py-1.5 rounded-lg transition-all shadow-sm block ml-auto mt-2"
                                            >
                                                <i className="fas fa-map-marked-alt fa-sm mr-1"></i> {trackingOrderId === order._id ? 'Close Map' : 'Track Driver'}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                                {trackingOrderId === order._id && (order.status === 'Out for Delivery' || order.status === 'Shipped') && (
                                    <tr className="block sm:table-row px-4 pb-4 sm:p-0">
                                        <td colSpan="5" className="px-0 sm:px-6 py-2 sm:py-4 bg-white sm:bg-slate-50 border-b border-slate-100 block sm:table-cell">
                                            <div className="rounded-xl overflow-hidden shadow-inner border border-slate-200 bg-white p-2">
                                                <DriverMapRoute 
                                                    storeLocation={storeLocation}
                                                    customerAddress={order.deliveryAddress}
                                                    driverLocation={order.driverLocation}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                            ))}
                            {orders.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-slate-400 font-medium">
                                        No orders found in the system.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className="flex justify-between items-center mt-4">
                    <button 
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                        disabled={currentPage === 1}
                        className="bg-white border border-slate-200 text-slate-600 px-4 py-2 rounded-xl text-xs font-bold shadow-sm hover:bg-slate-50 disabled:opacity-50 transition-colors"
                    >
                        Previous
                    </button>
                    <span className="text-xs font-bold text-slate-500">Page {currentPage} of {totalPages}</span>
                    <button 
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
                        disabled={currentPage === totalPages}
                        className="bg-white border border-slate-200 text-slate-600 px-4 py-2 rounded-xl text-xs font-bold shadow-sm hover:bg-slate-50 disabled:opacity-50 transition-colors"
                    >
                        Next
                    </button>
                </div>
            )}
            
            {selectedOrder && (
                <OrderEditModal 
                    order={selectedOrder} 
                    onClose={() => setSelectedOrder(null)} 
                    onSave={() => { fetchOrders(); setSelectedOrder(null); }}
                    showMessage={showMessage}
                />
            )}
        </div>
    );
};

export default AdminOrders;
